import { useState } from "react";
import { filterExercises, type Exercise } from "../core/exercises";
import type { Backend } from "../core/storage";
import { useBack } from "./back";
import { ExerciseEditor } from "./ExerciseEditor";
import { t } from "./texts";

/**
 * Full-screen multi-select search over the glossary, with "+ Neu" to create an exercise on the spot (it comes back
 * selected). Used by the template editor; todo 3 can use it for "Übung hinzufügen" in a running workout.
 */
export function ExercisePicker(props: {
  db: Backend;
  exercises: Exercise[];
  /** reloads the glossary after an exercise was created here */
  onCreated(e: Exercise): Promise<void>;
  onAdd(list: Exercise[]): void;
  onCancel(): void;
}) {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);
  useBack(props.onCancel);
  const toggle = (id: string) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const add = () => props.onAdd(selected.map((id) => props.exercises.find((e) => e.id === id)).filter((e): e is Exercise => !!e));

  if (creating) {
    return (
      <div className="fullscreen">
        <div className="page">
          <ExerciseEditor db={props.db} exercises={props.exercises} onCancel={() => setCreating(false)}
            onSaved={async (e) => {
              await props.onCreated(e);
              setSelected((s) => [...s, e.id]);
              setQuery("");
              setCreating(false);
            }} />
        </div>
      </div>
    );
  }

  const list = filterExercises(props.exercises, { query });
  return (
    <div className="fullscreen">
      <div className="page with-bottom-bar">
        <header className="titlebar">
          <button className="ghost" onClick={props.onCancel}>{t.cancel}</button>
          <button className="secondary" onClick={() => setCreating(true)}>{t.picker.create}</button>
        </header>
        <h1>{t.picker.title}</h1>
        <div className="sticky-filters">
          <input type="search" className="field" placeholder={t.exercises.search} value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        {list.length === 0 && <p className="muted-text">{t.exercises.none}</p>}
        <ul className="list">
          {list.map((e) => {
            const on = selected.includes(e.id);
            return (
              <li key={e.id}>
                <button className={"list-item pick" + (on ? " selected" : "")} aria-pressed={on} onClick={() => toggle(e.id)}>
                  <span className="name">{e.name}</span>
                  <span className="sub">{t.equipment(e.equipment)} · {t.bodyPart(e.bodyPart)} · {t.measure(e.measure)}</span>
                  <span className="check" aria-hidden>{on ? "✓" : ""}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
      <div className="bottom-bar">
        <button className="primary" disabled={!selected.length} onClick={add}>{t.picker.add(selected.length)}</button>
      </div>
    </div>
  );
}
